<template>
  <div class="attendance" :class="attendanceClass">

    <navigation
      @exit="exit" @chat-input-focus="onChatInputFocus"
      :navPanel.sync="navPanel"
      :class="navPanel ? `nav-panel-${navPanel.item}` : ''">
      >
    </navigation>

    <div class="attendance-content" v-bind:class="{ 'hide-if-short': hideContent }">
      <banner kind="warning" v-if="offline">{{ $t('offline') }}</banner>
      <banner kind="info" v-if="previewMode">{{ $t('previewMode') }}</banner>
      <component v-if="attendanceComponent"
        v-show="!showPlaceholder"
        v-bind:is="attendanceComponent"
        :webinarKey="webinarKey" :registrantKey="registrantKey"
        class="content-main"
      ></component>

      <content-placeholder
        v-show="showPlaceholder">
      </content-placeholder>
    </div>

    <div v-if="!embed" class="attendance-header">
      <img class="logo" :src="require('../../assets/goto-logo.svg')" />
      <div @click="exit">
        <label>{{ $t('attendance.exit') }}</label>
        <icon kind="leave-meeting" class="togo-icon-flip-horizontal"></icon>
      </div>
    </div>

    <modal-exit ref="modalExit"></modal-exit>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import g2wSession, {SESSION_STATUS} from '@/store/modules/g2w-session';
import g2wWebinar from '@/store/modules/g2w-webinar';
import survey from '@/store/modules/survey';
import errorReporter from '@/utils/error-reporter';
import windowUtils from '@/utils/window-utils';
import ModalExit from '@/components/modals/modal-exit';
import Navigation from '@/components/attendance/navigation';
import Icon from '@/components/togo/icon';
import Banner from '@/components/banner';
import ContentPlaceholder from './content-placeholder';

const AttendanceLive = () => import(/* webpackChunkName: "attendance-live" */ './attendance-live');
const AttendanceSimulive = () => import(/* webpackChunkName: "attendance-simulive" */ './attendance-simulive');

const Attendance = {
  name: 'Attendance',

  components: {
    AttendanceLive,
    AttendanceSimulive,
    Navigation,
    ContentPlaceholder,
    ModalExit,
    Icon,
    Banner
  },

  props: ['webinarKey', 'registrantKey', 'preview'],

  data() {
    return {
      navPanel: null,
      joinCount: 0,
      joinTime: 0,
      chatInputFocus: false
    };
  },

  computed: {
    attendanceClass() {
      return {
        embed: this.embed,
        'browser-not-ok': !this.isBrowserSupported,
        'attendance-in-session': this.inSession
      };
    },

    isBrowserSupported() {
      return this.$store.getters['system/isBrowserSupported'];
    },

    attendanceComponent() {
      if (this.isBrowserSupported) {
        if (this.$store.getters['g2wWebinar/isLive']) {
          return 'AttendanceLive';
        } else if (this.$store.getters['g2wWebinar/isSimulive']) {
          return 'AttendanceSimulive';
        }
      }
      return null;
    },

    sessionStatus() {
      return this.$store.state.g2wSession.sessionStatus;
    },

    inSession() {
      return this.$store.getters['g2wSession/inSession'];
    },

    left() {
      return this.sessionStatus === SESSION_STATUS.left;
    },

    ended() {
      return this.sessionStatus === SESSION_STATUS.ended;
    },

    activePoll() {
      return this.$store.state.g2wSession.polls.active;
    },

    navItem() {
      return this.navPanel ? this.navPanel.item : '';
    },

    showPlaceholder() {
      if (!this.isBrowserSupported || !this.inSession) {
        return true;
      }

      if (this.$store.state.recording) {
        return !this.$store.state.recording.clickNeeded && this.activePoll && !this.debugMode;
      }
      return this.activePoll && !this.debugMode;
    },

    hideContent() {
      return this.chatInputFocus || (this.navItem === 'chat' && this.isIOS);
    },

    ...mapState({
      embed: 'embed',
      debugMode: 'debugMode',
      offline: 'offline',
      previewMode: 'previewMode'
    }),

    ...mapGetters({
      experienceType: 'g2wWebinar/experienceType'
    })
  },

  watch: {
    experienceType: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.$telemetrySetContext({experienceType: newValue.toUpperCase()});
        }
      }
    },

    // call again the method if the route changes
    $route: 'join',

    inSession(newValue) {
      if (newValue) {
        windowUtils.addEventListener('beforeunload', this.beforeUnloadHandler);
        windowUtils.addEventListener('unload', this.unloadHandler);

        // get survey up front because if meeting ends it won't be available
        this.$store.dispatch('survey/checkAvailability', { webinarKey: this.webinarKey });
      } else {
        windowUtils.removeEventListener('unload', this.unloadHandler);
        windowUtils.removeEventListener('beforeunload', this.beforeUnloadHandler);
      }
    },

    left(newValue) {
      if (newValue) {
        this.postSession();
      }
    },

    ended(newValue) {
      if (newValue) {
        this.postSession();
      }
    },

    sessionStatus(newValue) {
      const msg = {
        session: {
          status: newValue
        }
      };

      windowUtils.postMessageToParent(msg);

      const elapsedMs = Date.now() - this.joinTime;

      switch (newValue) {
        case SESSION_STATUS.joining:
          this.$telemetryTrack('join attempt', {joinCount: this.joinCount});
          this.joinCount += 1;
          this.joinTime = Date.now();
          break;
        case SESSION_STATUS.waitStart:
          this.joinCount -= 1; // don't count that attempt
          break;
        case SESSION_STATUS.error:
          this.$telemetryTrack('join result', {result: 'failed', elapsedMs, scheduled: this.$store.getters['g2wSession/isScheduled']});
          this.$telemetryTrack('leave reason', {reason: 'failed', elapsedMs});
          break;
        case SESSION_STATUS.inSession:
          this.$telemetryTrack('join result', {result: 'success', elapsedMs, scheduled: this.$store.getters['g2wSession/isScheduled']});
          break;
        case SESSION_STATUS.left: {
          const reason = this.$store.state.g2wSession.closedBrowser ? 'closedBrowser' : 'left';
          this.$telemetryTrack('leave reason', {reason, elapsedMs});
          break;
        }
        case SESSION_STATUS.ended:
          this.$telemetryTrack('leave reason', {reason: 'ended', elapsedMs});
          break;
        default:
          break;
      }
    }
  },

  beforeCreate() {
    this.logger.log('creating');
    if (!this.$store.state.g2wSession) {
      this.$store.registerModule('g2wSession', g2wSession);
    }
    if (!this.$store.state.g2wWebinar) {
      this.$store.registerModule('g2wWebinar', g2wWebinar);
    }

    if (!this.$store.state.survey) {
      this.$store.registerModule('survey', survey);
    }
  },

  created() {
    if (this.preview) {
      this.$store.commit('setPreviewMode', { previewMode: true });
    }
    this.logger.setRootContext('webinarKey', this.webinarKey);
    this.logger.setRootContext('registrantKey', this.registrantKey);

    const context = {webinarKey: this.webinarKey, registrantKey: this.registrantKey};
    this.logger.log('created', {context});
    errorReporter.setContext(context);

    const uaParserResult = windowUtils.getParsedUserAgent();
    this.isIOS = /ios/i.test(uaParserResult.os.name);

    this.$store.dispatch('checkBrowser');
  },

  methods: {
    exit() {
      this.$refs.modalExit.show();
    },

    postSession() {
      const availability = this.$store.state.survey && this.$store.state.survey.surveyAvailability;

      if (availability) {
        const surveyUrl = this.$store.state.survey.surveyUrl;
        if (surveyUrl) {
          this.$telemetryTrack('redirect to survey', {surveyUrl});
          this.$router.push({name: 'survey', params: {surveyUrl}});
          return;
        }
      }

      this.$telemetryTrack('redirect to thankyou');
    },

    /* eslint-disable no-param-reassign */
    beforeUnloadHandler(event) {
      if (this.inSession) {
        event.preventDefault();
        event.returnValue = '';
      }
    },

    unloadHandler() {
      if (this.inSession) {
        this.$store.dispatch('g2wSession/leave', {closedBrowser: true, userInitiated: true});
      }
    },

    onChatInputFocus(focused) {
      this.chatInputFocus = focused;
    }
  }
};

export default Attendance;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  @import 'src/scss/mixins'

  .attendance
    display: flex
    min-height: 1px /* workaround Firefox flex issue */
    max-height: 100%
    @include layout-horizontal
      height: 100%
    @include layout-vertical
      flex-direction: column-reverse
      justify-content: flex-end

      &:not(.attendance-in-session)
        height: 100%

      &.browser-not-ok
        .navigation
          display: none

        .attendance-content
          margin-top: auto
          margin-bottom: auto

    .attendance-in-session
      width: 100%
      height: 100%
      display: block

    .attendance-content
      width: 100%
      @include layout-horizontal
        display: flex
        flex-direction: column
        height: 100%

      .content-main
        display: flex
        align-items: center
        justify-content: center
        background: $content-background
        &.attendance-classic, &.attendance-broadcast
          min-height: 40%

        @include layout-horizontal
          height: 100%
          max-height: 100%
          flex: 1

      .content-placeholder
        width: 100%
        min-height: 250px

        @include layout-horizontal
          height: 100%

    .attendance-header
      @include layout-horizontal
        display: none
      width: 100%
      height: 33px
      display: flex
      align-items: center
      justify-content: space-between
      padding: 10px
      img
        width: 30px
      div
        cursor: pointer
        line-height: 21px
        display: flex
        align-items: center
        label
          cursor: pointer
          margin: 0
          font-size: 13px
          color: #2ac8f6
          text-transform: uppercase
        i
          margin-left: 3px
          width: 20px

    &.embed
      .content-placeholder
        top: 0

    @include layout-vertical
      .nav-panel-polls + .attendance-content
          display: none

      .hide-if-short
        @media (max-height: 650px)
          display: none

  @include media-breakpoint-down(sm)
    .attendance
      padding-left: 0
      padding-right: 0
</style>
